// hooks/useMessagePagination.js
import { useState, useCallback, useRef } from 'react';
import { supabase } from '../lib/supabase';

const MESSAGES_PER_PAGE = 25;
const MAX_MESSAGES_IN_MEMORY = 100; // Keep max 100 messages in memory to prevent memory bloat

export const useMessagePagination = (isGlobalChat = false) => {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [error, setError] = useState(null);
  const oldestMessageRef = useRef(null);
  const conversationIdRef = useRef(null);

  // Reset pagination state (when switching conversations)
  const resetPagination = useCallback(() => {
    setMessages([]);
    setHasMore(true);
    setError(null);
    oldestMessageRef.current = null;
  }, []);

  // Load initial messages (most recent) - using the proven fallback approach
  const loadInitialMessages = useCallback(async (conversationId = null) => {
    setLoading(true);
    setError(null);
    conversationIdRef.current = conversationId;

    try {
      // Use the same approach as ChatContext - direct query without JOIN, then fetch users separately
      let query = supabase
        .from('chat_messages')
        .select('*')
        .order('created_at', { ascending: false })
        .limit(MESSAGES_PER_PAGE);

      if (isGlobalChat) {
        query = query.eq('is_dm', false).is('match_id', null);
      } else if (conversationId) {
        query = query
          .eq('conversation_id', conversationId)
          .eq('is_dm', true);
      } else {
        throw new Error('Conversation ID required for DM messages');
      }

      const { data: messagesData, error: messagesError } = await query;
      if (messagesError) throw messagesError;

      if (messagesData && messagesData.length > 0) {
        // Fetch user data separately (same as ChatContext fallback)
        const userIds = [...new Set(messagesData.map(m => m.user_id))];
        const { data: usersData, error: usersError } = await supabase
          .from('users')
          .select('id, username, avatar_url, country_code, display_name')
          .in('id', userIds);

        if (usersError) throw usersError;

        // Create user lookup map
        const userMap = (usersData || []).reduce((acc, user) => {
          acc[user.id] = user;
          return acc;
        }, {});

        // Combine messages with user data
        const messagesWithUsers = messagesData.map(message => ({
          ...message,
          users: userMap[message.user_id] || null
        }));

        // Reverse to show oldest first in UI
        const sortedMessages = messagesWithUsers.reverse();
        
        setMessages(sortedMessages);
        setHasMore(messagesData.length === MESSAGES_PER_PAGE);
        
        if (sortedMessages.length > 0) {
          oldestMessageRef.current = sortedMessages[0].created_at;
        }
      } else {
        setMessages([]);
        setHasMore(false);
      }

    } catch (error) {
      console.error('Failed to load initial messages:', error);
      setError('Failed to load messages');
    } finally {
      setLoading(false);
    }
  }, [isGlobalChat]);

  // Load older messages (pagination) - using the same proven approach
  const loadOlderMessages = useCallback(async () => {
    if (!hasMore || loading || !oldestMessageRef.current) return;

    setLoading(true);
    setError(null);

    try {
      let query = supabase
        .from('chat_messages')
        .select('*')
        .lt('created_at', oldestMessageRef.current)
        .order('created_at', { ascending: false })
        .limit(MESSAGES_PER_PAGE);

      if (isGlobalChat) {
        query = query.eq('is_dm', false).is('match_id', null);
      } else if (conversationIdRef.current) {
        query = query
          .eq('conversation_id', conversationIdRef.current)
          .eq('is_dm', true);
      }

      const { data: messagesData, error: messagesError } = await query;
      if (messagesError) throw messagesError;

      if (messagesData && messagesData.length > 0) {
        // Fetch user data separately
        const userIds = [...new Set(messagesData.map(m => m.user_id))];
        const { data: usersData, error: usersError } = await supabase
          .from('users')
          .select('id, username, avatar_url, country_code, display_name')
          .in('id', userIds);

        if (usersError) throw usersError;

        // Create user lookup map
        const userMap = (usersData || []).reduce((acc, user) => {
          acc[user.id] = user;
          return acc;
        }, {});

        // Combine messages with user data
        const messagesWithUsers = messagesData.map(message => ({
          ...message,
          users: userMap[message.user_id] || null
        }));

        // Reverse to show oldest first, then prepend to existing messages
        const olderMessages = messagesWithUsers.reverse();
        
        setMessages(prevMessages => {
          const newMessages = [...olderMessages, ...prevMessages];
          
          // Trim messages if we exceed max memory limit
          if (newMessages.length > MAX_MESSAGES_IN_MEMORY) {
            return newMessages.slice(-MAX_MESSAGES_IN_MEMORY);
          }
          
          return newMessages;
        });
        
        oldestMessageRef.current = olderMessages[0].created_at;
        setHasMore(messagesData.length === MESSAGES_PER_PAGE);
      } else {
        setHasMore(false);
      }

    } catch (error) {
      console.error('Failed to load older messages:', error);
      setError('Failed to load older messages');
    } finally {
      setLoading(false);
    }
  }, [hasMore, loading, isGlobalChat]);

  // Add new message (for real-time updates)
  const addMessage = useCallback((newMessage) => {
    setMessages(prevMessages => {
      // Check if message already exists (prevent duplicates)
      const exists = prevMessages.some(msg => msg.id === newMessage.id);
      if (exists) return prevMessages;

      const newMessages = [...prevMessages, newMessage];
      
      // Trim old messages if we exceed max memory limit
      if (newMessages.length > MAX_MESSAGES_IN_MEMORY) {
        return newMessages.slice(-MAX_MESSAGES_IN_MEMORY);
      }
      
      return newMessages;
    });
  }, []);

  return {
    messages,
    loading,
    hasMore,
    error,
    loadInitialMessages,
    loadOlderMessages,
    addMessage,
    resetPagination
  };
};